import { FC, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Save, Cancel } from "@mui/icons-material";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import ControlledSelectField from "../../components/controlledComponents/ControlledSelectField";
import { TCourse } from "../../../apis/academic/academic.interfaces";
import {
  useCreateCourseHook,
  useUpdateCourseHook,
} from "../../../hooks/useAcademicHook";
import { useObepListHook } from "../../../hooks/useObepHook";
import { logger } from "../../../utils/common";

const courseSchema = z.object({
  name: z
    .string()
    .min(1, "Course name is required")
    .min(2, "Course name must be at least 2 characters")
    .max(200, "Course name must not exceed 200 characters"),
  obepId: z.string().min(1, "Select an OBEP policy"),
  description: z
    .string()
    .max(500, "Description must not exceed 500 characters")
    .optional()
    .or(z.literal("")),
  startGrade: z
    .string()
    .max(80, "Grade label too long")
    .optional()
    .or(z.literal("")),
  endGrade: z
    .string()
    .max(80, "Grade label too long")
    .optional()
    .or(z.literal("")),
  status: z.enum(["active", "inactive"], {
    errorMap: () => ({ message: "Please select a valid status" }),
  }),
});

type CourseFormData = z.infer<typeof courseSchema>;

interface ManageCourseDialogProps {
  open: boolean;
  onClose: () => void;
  course?: TCourse | null;
}

const ManageCourseDialog: FC<ManageCourseDialogProps> = ({
  open,
  onClose,
  course,
}) => {
  const createCourseMutation = useCreateCourseHook();
  const updateCourseMutation = useUpdateCourseHook();
  const { data: obepRows, isLoading: obepLoading } = useObepListHook({
    page: 1,
    limit: 100,
  });
  const isEditMode = !!course;

  const obepOptions = useMemo(
    () =>
      (obepRows ?? []).map((r) => ({
        key: r.obepId,
        value: r.obepName,
      })),
    [obepRows],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CourseFormData>({
    resolver: zodResolver(courseSchema),
    defaultValues: {
      name: "",
      obepId: "",
      description: "",
      startGrade: "",
      endGrade: "",
      status: "active",
    },
  });

  useEffect(() => {
    if (course) {
      reset({
        name: course.name,
        obepId: course.obepId,
        description: course.description || "",
        startGrade: course.startGrade || "",
        endGrade: course.endGrade || "",
        status:
          course.status === "inactive" ? "inactive" : ("active" as const),
      });
    } else {
      reset({
        name: "",
        obepId: obepOptions[0]?.key ?? "",
        description: "",
        startGrade: "",
        endGrade: "",
        status: "active",
      });
    }
  }, [course, reset, open, obepOptions]);

  const onSubmit = async (data: CourseFormData) => {
    try {
      if (isEditMode && course) {
        await updateCourseMutation.mutateAsync({
          id: course.id,
          request: {
            name: data.name,
            description: data.description || undefined,
            startGrade: data.startGrade || undefined,
            endGrade: data.endGrade || undefined,
            status: data.status,
          },
        });
        logger.show_success("Course updated successfully!");
      } else {
        await createCourseMutation.mutateAsync({
          name: data.name,
          obepId: data.obepId,
          description: data.description || undefined,
          startGrade: data.startGrade || undefined,
          endGrade: data.endGrade || undefined,
          status: data.status,
        });
        logger.show_success("Course created successfully!");
      }
      onClose();
    } catch (error) {
      logger.show_error(
        error instanceof Error
          ? error.message
          : `Failed to ${isEditMode ? "update" : "create"} course. Please try again.`,
      );
    }
  };

  const isLoading =
    isSubmitting ||
    createCourseMutation.isPending ||
    updateCourseMutation.isPending;
  const isError = createCourseMutation.isError || updateCourseMutation.isError;
  const errorMessage =
    createCourseMutation.error?.message || updateCourseMutation.error?.message;

  const cannotCreateWithoutObep =
    !isEditMode && !obepLoading && obepOptions.length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid="manage-course-dialog"
    >
      <DialogTitle data-testid="manage-course-dialog-title">
        {isEditMode ? "Edit Course" : "Add New Course"}
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)} data-testid="course-form">
        <DialogContent data-testid="course-form-content">
          {isError && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              data-testid="course-form-error"
            >
              {errorMessage ||
                `Failed to ${isEditMode ? "update" : "create"} course. Please try again.`}
            </Alert>
          )}

          {cannotCreateWithoutObep && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Create an OBEP record first — each course must belong to a board /
              policy.
            </Alert>
          )}

          <Grid
            container
            spacing={3}
            sx={{ mt: 1 }}
            data-testid="course-form-container"
          >
            <ControlledTextField
              control={control as any}
              name="name"
              id="course-name"
              label="Course Name"
              required
              errorMessage={errors.name?.message}
              inputProps={{ "data-testid": "course-name-input" }}
            />

            {isEditMode && course ? (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  OBEP (fixed after create):{" "}
                  {obepOptions.find((o) => o.key === course.obepId)?.value ??
                    course.obepId}
                </Typography>
              </Grid>
            ) : (
              <ControlledSelectField<CourseFormData>
                control={control}
                name="obepId"
                id="course-obep"
                label="OBEP / Board policy"
                itemList={obepOptions}
                isRequired
                isGridItem
                errorMessage={errors.obepId?.message}
              />
            )}

            <ControlledTextField
              control={control as any}
              name="startGrade"
              id="course-start-grade"
              label="Start grade (optional)"
              errorMessage={errors.startGrade?.message}
              inputProps={{ "data-testid": "course-start-grade-input" }}
            />

            <ControlledTextField
              control={control as any}
              name="endGrade"
              id="course-end-grade"
              label="End grade (optional)"
              errorMessage={errors.endGrade?.message}
              inputProps={{ "data-testid": "course-end-grade-input" }}
            />

            <ControlledSelectField<CourseFormData>
              control={control}
              name="status"
              id="course-status"
              label="Status"
              itemList={[
                { key: "active", value: "Active" },
                { key: "inactive", value: "Inactive" },
              ]}
              isRequired
              isGridItem
              errorMessage={errors.status?.message}
            />

            <Grid item xs={12}>
              <ControlledTextField
                control={control as any}
                name="description"
                id="course-description"
                label="Description"
                multiline
                rows={2}
                errorMessage={errors.description?.message}
                inputProps={{ "data-testid": "course-description-input" }}
                isGridItem={false}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions data-testid="course-dialog-actions">
          <Button
            onClick={onClose}
            disabled={isLoading}
            startIcon={<Cancel />}
            data-testid="cancel-course-button"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isLoading || cannotCreateWithoutObep}
            startIcon={isLoading ? <CircularProgress size={16} /> : <Save />}
            data-testid="save-course-button"
          >
            {isLoading
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Course"
                : "Create Course"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageCourseDialog;
